import React from 'react'
import { useSearchParams } from 'react-router';
import { jsPDF } from 'jspdf';
import scrollToTop from '../assets/scrollToTop';

const API_URL = window.ArmaTuPCConfig?.apiUrl || process.env.REACT_APP_API_URL;
const LOGO_URL = 'https://universosgamers.com.ar/wp-content/uploads/2025/10/logo-1758002967.png';
 
// Colores del diseño actual del sitio (index.css), para que el PDF se vea igual.
const COLOR_FONDO = [9, 15, 25];       // #090F19 - fondo de la app
const COLOR_CARD = [28, 31, 46];       // #1C1F2E - fondo de las cards
const COLOR_BORDE = [47, 54, 87];      // #2f3657 - borde de las cards
const COLOR_ACENTO = [111, 170, 255];  // #6FAAFF - etiquetas de categoría
const COLOR_TOTAL = [110, 0, 206];     // #6E00CE - barra del total
 
// Convierte una imagen (misma URL/dominio que el sitio) a un data URL, para
// poder meterla adentro del PDF con doc.addImage(). Si por algo falla (la
// imagen no carga, cambió de URL, etc.), el PDF se genera igual, sin logo.
const cargarImagenComoDataURL = (url) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            canvas.getContext('2d').drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = url;
    });
}

const HeaderComponent = (props) => {
    const { steps, currentStep, setSteps } = props;
    const [searchParams, setSearchParams] = useSearchParams();

    const isFinal = currentStep.tag === 'final';
    const missingRequired = steps.filter(step => step.tag !== 'final' && step.required && !step.data);

    const isComprable = isFinal && missingRequired.length === 0;

    const getTotal = () => {
        return steps.reduce((accumulator, currentValue) => accumulator + ((Number(currentValue.data?.price) * currentValue.data?.cantidad) || 0), 0) || 0;
    }

    const formatMoney = (num) => `$${new Intl.NumberFormat("es-AR", {minimumFractionDigits: 2}).format(Number(num).toFixed(2))}`;

    const prevStep = () => {
        if(currentStep.tag === 'final') {
            searchParams.set('step', 9);
            setSearchParams(searchParams, { replace: true });
        } else {
            searchParams.set('step', Number(searchParams.get('step')) - 1);
            setSearchParams(searchParams, { replace: true });
        }

        scrollToTop();
    }

    const nextStep = () => {
        if(isFinal)
            return buyNow();

        searchParams.set('step', Number(searchParams.get('step')) + 1);
        setSearchParams(searchParams, { replace: true });

        scrollToTop();
    }

    const verResumen = () => {
        searchParams.set('step', 15);
        setSearchParams(searchParams, { replace: true });
 
        scrollToTop();
    }

    const descargarPDF = async () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
 
        const pintarFondo = () => {
            doc.setFillColor(...COLOR_FONDO);
            doc.rect(0, 0, pageWidth, pageHeight, 'F');
        }
 
        const truncar = (texto, maxWidth) => {
            if (doc.getTextWidth(texto) <= maxWidth) return texto;
            let recortado = texto;
            while (recortado.length > 0 && doc.getTextWidth(recortado + '...') > maxWidth) {
                recortado = recortado.slice(0, -1);
            }
            return recortado + '...';
        }
 
        pintarFondo();
 
        let y = 20;
 
        try {
            const logoData = await cargarImagenComoDataURL(LOGO_URL);
            doc.addImage(logoData, 'PNG', 14, y - 6, 22, 22);
        } catch (e) {
            // Si el logo no carga, seguimos igual sin romper la descarga.
        }
 
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.text('Universo Gamer', 42, y);
        doc.setTextColor(...COLOR_ACENTO);
        doc.setFontSize(11);
        doc.text('Resumen de tu PC armada', 42, y + 7);
 
        y += 26;
 
        const componentes = steps.filter(step => step.tag !== 'final' && step.data);
        const alturaCard = 20;
 
        componentes.forEach(step => {
            if (y + alturaCard > pageHeight - 30) {
                doc.addPage();
                pintarFondo();
                y = 20;
            }
 
            const cantidad = step.data.cantidad || 1;
            const subtotal = Number(step.data.price) * cantidad;
 
            doc.setFillColor(...COLOR_CARD);
            doc.setDrawColor(...COLOR_BORDE);
            doc.roundedRect(14, y, pageWidth - 28, alturaCard, 3, 3, 'FD');
 
            doc.setTextColor(...COLOR_ACENTO);
            doc.setFontSize(8);
            doc.text(step.nombre.toUpperCase(), 20, y + 7);
 
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(10);
            doc.text(truncar(step.data.name, pageWidth - 95), 20, y + 15);
 
            doc.setFontSize(10);
            doc.text(`x${cantidad}`, pageWidth - 62, y + 12);
 
            doc.setFont(undefined, 'bold');
            doc.text(formatMoney(subtotal), pageWidth - 20, y + 12, { align: 'right' });
            doc.setFont(undefined, 'normal');
 
            y += alturaCard + 6;
        });
 
        if (y + 20 > pageHeight - 20) {
            doc.addPage();
            pintarFondo();
            y = 20;
        }
 
        doc.setFillColor(...COLOR_TOTAL);
        doc.roundedRect(14, y, pageWidth - 28, 18, 3, 3, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.text('TOTAL', 20, y + 12);
        doc.setFontSize(14);
        doc.text(formatMoney(getTotal()), pageWidth - 20, y + 12, { align: 'right' });
 
        doc.save('resumen-pc.pdf');
    }

    const deleteAll = () => {
        searchParams.set('step', 1);
        steps.forEach(step => {
            searchParams.delete(step.tag);
        });
        setSteps(prevSteps => prevSteps.map(step => ({...step, data: undefined})));
        setSearchParams(searchParams, { replace: true });
    }

    const buyNow = async () => {
        if (missingRequired.length > 0) return;

        const items = steps
            .filter(step => step.data)
            .map(step => ({ id: step.data.id, quantity: step.data.cantidad || 1 }));
 
        if (items.length === 0) return;

        try {
            const response = await fetch(`${API_URL}/agregar-carrito`, {
                method: 'POST',
                headers: { 'Content-type': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({ items }),
            });
 
            const result = await response.json();
 
            if (!response.ok || result.error) {
                throw new Error(result.error || 'No se pudo agregar los productos al carrito.');
            }
 
            window.location.href = result.cart_url;
        } catch (err) {
            console.error('Error al agregar productos al carrito:', err);
        }
    }

    return (
        <div className="armado-info">
            <div className="top-info">
                <div className="step-info">
                    {!isFinal && 
                        <h3 className="info-title">
                            Elegí tu {currentStep.nombre}
                        </h3>}
                    <span className={isFinal ? "info-description description-final" : "info-description"}>{currentStep.descripcion}</span>
                </div>
                <div className="total-info">
                    Total:
                    <span className="info-price">{formatMoney(getTotal())}</span>
                </div>
            </div>
            <div className="bottom-info">
                <div className="info-buttons">
                    <button className="prev-button" onClick={prevStep} disabled={currentStep.id === 1}>PASO ANTERIOR</button>
                    <button className="next-button" onClick={nextStep} disabled={isFinal && missingRequired.length > 0}>{!isFinal ? "PASO SIGUIENTE" : "COMPRAR AHORA"}</button>
                    {
                        isFinal ? 
                            <button className="resumen-button" onClick={descargarPDF}>DESCARGAR PDF</button> :
                            <button className="resumen-button" onClick={verResumen}>VER RESUMEN</button>
                    }
                </div>
                <button className="delete-button" onClick={deleteAll}>REINICIAR</button>
            </div>
        </div>
    )
}

export default HeaderComponent
